/**
 * Utility functions for importing and exporting labels to/from Excel
 * Using ExcelJS for better formatting support
 */
import ExcelJS from "exceljs"
import {
  LabelItem,
  LabelType,
  parseQuestionLabel,
  buildQuestionLabel,
} from "./labelUtils"

/**
 * Result structure for label import validation
 */
interface ImportValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

/**
 * Result structure for label import
 */
export interface LabelImportResult {
  labels: Partial<LabelItem>[]
  validationResult: ImportValidationResult
}

/**
 * Exports labels to an Excel file with only the Content column editable (using ExcelJS)
 *
 * @param labels - Array of label items to export
 * @param filename - Name of the file to create (without extension)
 * @returns True if export was successful, false otherwise
 */
export const exportLabelsToExcel = async (
  labels: LabelItem[],
  filename: string
): Promise<boolean> => {
  try {
    // Create a new workbook
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet("Labels")

    // Define columns. For Question Labels the editable Content cell holds ONLY the
    // standalone label (variable name); the wording is editable in the
    // "Question Text" column. For lists/range lists Content holds the full block.
    worksheet.columns = [
      { header: "Name (Reference Only)", key: "name", width: 25 },
      { header: "Type", key: "type", width: 15 },
      { header: "Content (editable) - For Questions this is the LABEL only; for Lists/Range Lists it is the full block. Renaming here updates all references.", key: "content", width: 80 },
      { header: "Question Text (editable for Questions)", key: "questionText", width: 60 },
      { header: "ID", key: "id", width: 10, hidden: true },
      { header: "Line Numbers", key: "lineNumbers", width: 15, hidden: true },
      { header: "Reference Lines", key: "referenceLines", width: 15, hidden: true },
    ]

    // Add label data
    labels.forEach((label) => {
      const lineNumbers = label.lineNumbers.join(", ")
      const referenceLines = label.referenceLineNumbers?.join(", ") || "N/A"

      // Split question labels so the editable Content cell is just the label.
      let contentCell = label.content
      let questionText = ""
      if (label.type === LabelType.QUESTION) {
        const parsed = parseQuestionLabel(label.content)
        contentCell = parsed.label
        questionText = parsed.text
      }

      worksheet.addRow({
        name: label.name,
        content: contentCell,
        questionText: questionText,
        id: label.id,
        type: label.type,
        lineNumbers: lineNumbers,
        referenceLines: referenceLines,
      })
    })

    // Apply wrap text to all cells and set protection
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        cell.alignment = {
          wrapText: true,
          vertical: "top",
          horizontal: "left",
        }

        // Determine the row's type (LabelType values: "Question Label", "Exceptions", ...)
        const typeCell = row.getCell(2) // Type column
        const typeValue = typeCell.value?.toString()
        const isExceptionsRow = typeValue === LabelType.EXCEPTIONS
        const isQuestionRow = typeValue === LabelType.QUESTION

        // Unlock the Content column (col 3) for data rows except EXCEPTIONS, and
        // unlock the Question Text column (col 4) for Question Label rows.
        if (rowNumber > 1 && colNumber === 3 && !isExceptionsRow) {
          cell.protection = { locked: false }
        } else if (rowNumber > 1 && colNumber === 4 && isQuestionRow) {
          cell.protection = { locked: false }
        } else {
          // Everything else - locked (Name, Type, headers, hidden columns, EXCEPTIONS content)
          cell.protection = { locked: true }
        }
      })
    })

    // Protect the worksheet (no password for ease of use)
    await worksheet.protect("", {
      selectLockedCells: true,
      selectUnlockedCells: true,
      formatCells: false,
      formatColumns: false,
      formatRows: false,
      insertRows: false,
      insertColumns: false,
      deleteRows: false,
      deleteColumns: false,
    })

    // Generate Excel file as buffer
    const buffer = await workbook.xlsx.writeBuffer()

    // Create blob and download
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)

    return true
  } catch (error) {
    console.error("Error exporting labels to Excel:", error)
    return false
  }
}

/**
 * Validates content format based on label type.
 *
 * Patterns intentionally MIRROR the questionnaire parser (parseLabelsAndLists)
 * rather than being stricter — label and list names can contain spaces, slashes,
 * parentheses, etc. (e.g. "Scale Recommendation", "NPS - Home/investment loan").
 *
 * @param content - The content to validate
 * @param type - The label type
 * @returns Validation result with errors
 */
export const validateLabelContent = (
  content: string,
  type: LabelType
): ImportValidationResult => {
  const errors: string[] = []
  const warnings: string[] = []

  if (!content || content.trim() === "") {
    errors.push("Content cannot be empty")
    return { isValid: false, errors, warnings }
  }

  switch (type) {
    case LabelType.QUESTION: {
      // Must start with "Q <name>:" — name may contain any characters.
      const questionMatch = content.match(/^Q\s+(.+?):/)
      if (!questionMatch) {
        errors.push(
          'Question labels must start with "Q <name>:" format (e.g., "Q Team: Which team...")'
        )
      }
      break
    }

    case LabelType.LIST: {
      // Must start with "LIST <name>"
      const listMatch = content.match(/^LIST\s+(.+)/)
      if (!listMatch) {
        errors.push('Lists must start with "LIST <name>" format')
      } else {
        // Check if there are list items
        const lines = content.split("\n")
        if (lines.length < 2) {
          warnings.push("List appears to have no items")
        }
      }
      break
    }

    case LabelType.RANGE_LIST: {
      // Must start with "RANGE LIST <name>: <range>" — name may contain spaces etc.
      const rangeMatch = content.match(/^RANGE\s+LIST\s+(.+?):\s*(.+)/)
      if (!rangeMatch) {
        errors.push(
          'Range lists must start with "RANGE LIST <name>: <range>" format (e.g., "RANGE LIST Scale: 1..5")'
        )
      } else {
        // Check if there are scale items
        const lines = content.split("\n")
        if (lines.length < 2) {
          warnings.push("Range list appears to have no scale items")
        }
      }
      break
    }

    case LabelType.EXCEPTIONS: {
      // Must start with "EXCEPTIONS" or "LIST EXCEPTIONS"
      if (
        !content.trim().startsWith("EXCEPTIONS") &&
        !content.trim().startsWith("LIST EXCEPTIONS")
      ) {
        errors.push('Exceptions must start with "EXCEPTIONS" or "LIST EXCEPTIONS"')
      }
      break
    }

    default:
      errors.push(`Unknown label type: ${type}`)
  }

  return { isValid: errors.length === 0, errors, warnings }
}

/**
 * Imports labels from an Excel file (using ExcelJS)
 *
 * @param file - The Excel file to import
 * @param currentLabels - The current set of labels (for validation)
 * @returns Promise that resolves to import result with labels and validation errors
 */
export const importLabelsFromExcel = async (
  file: File,
  currentLabels: LabelItem[]
): Promise<LabelImportResult> => {
  try {
    // Read file as array buffer
    const arrayBuffer = await file.arrayBuffer()

    // Load workbook with ExcelJS
    const workbook = new ExcelJS.Workbook()
    await workbook.xlsx.load(arrayBuffer)

    // Get the first worksheet
    const worksheet = workbook.getWorksheet(1)
    if (!worksheet) {
      throw new Error("Excel file has no sheets")
    }

    const importedLabels: Partial<LabelItem>[] = []
    const allErrors: string[] = []
    const allWarnings: string[] = []

    // Track which labels were found in the import
    const foundIds = new Set<string>()

    // Get column headers from first row
    const headerRow = worksheet.getRow(1)
    const headers: Record<string, number> = {}
    headerRow.eachCell((cell, colNumber) => {
      const value = cell.value?.toString() || ""
      // Map column headers to column numbers
      if (value.includes("Name") && !value.includes("Line Numbers")) headers.name = colNumber
      if (value.includes("Content")) headers.content = colNumber
      if (value.includes("Question Text")) headers.questionText = colNumber
      if (value === "Type") headers.type = colNumber
      if (value === "ID") headers.id = colNumber
      if (value === "Line Numbers") headers.lineNumbers = colNumber
      if (value === "Reference Lines") headers.referenceLines = colNumber
    })

    // Process each row starting from row 2 (row 1 is header)
    worksheet.eachRow((row, rowNumber) => {
      // Skip header row
      if (rowNumber <= 1) return

      const id = row.getCell(headers.id)?.value?.toString() || ""
      const type = row.getCell(headers.type)?.value?.toString() as LabelType
      const content = row.getCell(headers.content)?.value?.toString() || ""

      if (!id || !type || !content) {
        allErrors.push(
          `Row ${rowNumber}: Missing required fields (ID, Type, or Content)`
        )
        return
      }

      foundIds.add(id)

      // Find the corresponding current label
      const currentLabel = currentLabels.find((label) => label.id === id)

      if (!currentLabel) {
        allErrors.push(
          `Row ${rowNumber}: Label with ID "${id}" not found in current labels`
        )
        return
      }

      // Validate that type hasn't changed
      if (type !== currentLabel.type) {
        allErrors.push(
          `Row ${rowNumber}: Type cannot be changed from "${currentLabel.type}" to "${type}"`
        )
        return
      }

      // For Question Labels the Content cell holds just the standalone label and
      // the wording lives in the Question Text column. Rebuild the full
      // "Q <label>: <text>" line before validating, using the edited wording when
      // present (falling back to the current wording). Older exports may still
      // carry the full line in Content — accept that too.
      let effectiveContent = content
      if (type === LabelType.QUESTION) {
        const looksFull = /^Q\s+.+?:/.test(content.trim())
        if (looksFull) {
          effectiveContent = content.trim()
        } else {
          const editedText = headers.questionText
            ? (row.getCell(headers.questionText)?.value?.toString() || "").trim()
            : ""
          const text =
            editedText || parseQuestionLabel(currentLabel.content).text
          effectiveContent = buildQuestionLabel(content, text)
        }
      }

      // Validate content format
      const validationResult = validateLabelContent(effectiveContent, type)
      if (!validationResult.isValid) {
        allErrors.push(
          `Row ${rowNumber} (${currentLabel.name}): ${validationResult.errors.join(", ")}`
        )
        return
      }

      if (validationResult.warnings.length > 0) {
        allWarnings.push(
          `Row ${rowNumber} (${currentLabel.name}): ${validationResult.warnings.join(", ")}`
        )
      }

      // Only include labels that have changed
      if (effectiveContent !== currentLabel.content) {
        importedLabels.push({
          id,
          content: effectiveContent,
          type,
        })
      }
    })

    // Check if any labels are missing from the import
    const missingLabels = currentLabels.filter(
      (label) => !foundIds.has(label.id)
    )
    if (missingLabels.length > 0) {
      allWarnings.push(
        `The following labels were not found in the import and will remain unchanged: ${missingLabels.map((l) => l.name).join(", ")}`
      )
    }

    const validationResult: ImportValidationResult = {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings,
    }

    return {
      labels: importedLabels,
      validationResult,
    }
  } catch (error) {
    console.error("Error importing labels from Excel:", error)
    throw error instanceof Error ? error : new Error(String(error))
  }
}
